import type { MouseEvent } from 'react';
import { useState } from 'react';

import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreateSchoolSpeakerMutation } from '@/api/endpoints/schools/schoolSubjectionParams/speakers';
import { DataSection, ImageUploadField, TextAreaField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { SPEAKER_VALIDATION_SCHEMA } from '../../consts';
import type { TFormData } from '../../types';

import styles from './SpeakerModals.module.scss';

type TProps = {
  schoolId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
};

export const CreateSpeakerModal = ({ schoolId, isModalOpen, handleCancel }: TProps) => {
  const [createSpeaker, { isLoading: isSpeakerCreating }] = useCreateSchoolSpeakerMutation();
  const sendApiRequest = useApiRequest();

  const methods = useForm<TFormData>({
    defaultValues: {
      fullname: '',
      bio: '',
      photo: null,
    },
    resolver: yupResolver(SPEAKER_VALIDATION_SCHEMA),
  });

  const { watch, resetField, handleSubmit } = methods;

  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const photo = watch('photo');

  const getImageName = () => {
    if (!photo) {
      return '';
    }

    return photo.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    resetField('photo');
    setImageUrl('');
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  const handleSaveSpeaker = handleSubmit(({ fullname, bio, photo }) => {
    sendApiRequest({
      successMessage: 'Спикер успешно создан',
      apiRequestCallback: () =>
        createSpeaker({
          schoolId,
          data: {
            full_name: fullname,
            job_title: 'Спикер',
            bio,
            photo: photo?.signed_id ? photo.signed_id : null,
          },
        }),
      successCallback: handleCancel,
    });
  });

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isSpeakerCreating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Добавление спикера</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="ФИО"
                isTitleRequired
              >
                <TextAreaField
                  fieldName="fullname"
                  placeholder="ФИО"
                />
              </DataSection>
              <DataSection
                titleText="Описание"
                isTitleRequired
              >
                <TextAreaField
                  fieldName="bio"
                  placeholder="Описание"
                />
              </DataSection>
              <DataSection
                titleText="Фото"
                isTitleRequired
              >
                <ImageUploadField
                  accept="image/png,image/jpeg,image/jpg"
                  name="Изображение"
                  fieldName="photo"
                  className={photo ? styles['image-uploaded'] : ''}
                  requestCallback={imageRequestCallback}
                  showUploadList={false}
                />
                {photo && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setIsImagePreviewOpen(true)}
                      type="text"
                      icon={<PaperClipOutlined />}
                      className={styles['image-button-wrapper']}
                    >
                      {getImageName()}
                      <DeleteOutlined
                        className={styles['remove-image-icon']}
                        onClick={removeUploadedImage}
                      />
                    </Button>
                    <Image
                      alt="Фото спикера"
                      style={{ display: 'none' }}
                      preview={{
                        open: isImagePreviewOpen,
                        src: imageUrl,
                        onOpenChange: (value) => setIsImagePreviewOpen(value),
                      }}
                    />
                  </>
                )}
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                type="dashed"
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleSaveSpeaker}
                type="primary"
                className={styles.button}
                disabled={isSpeakerCreating}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
